/**
 * App-wide banner while the background catalog refresh runs, so the run stays visible
 * after navigating away from the catalog page.
 *
 * This is also the single mount point of the terminal effect (toast + cache
 * invalidation): the banner is always mounted in MainLayout, so the effect cannot
 * double-fire with the panel on the catalog page.
 * For Media Devoted | Created By Ciro Autiero
 */

import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

import { LoadingSpinner } from '@/components/ui';
import { useCatalogRefreshJob } from '@/hooks/smswiser/catalog/refresh-job';
import { useCatalogRefreshTerminalEffect } from './useCatalogRefreshTerminalEffect';

export function CatalogRefreshBanner() {
  const { data, dataUpdatedAt } = useCatalogRefreshJob();
  useCatalogRefreshTerminalEffect(data, dataUpdatedAt);

  if (!data?.isRunning) return null;
  const { progress } = data;

  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-border bg-primary/5 px-4 py-2 text-sm">
      <LoadingSpinner size="sm" className="text-primary" />
      <span className="font-medium text-foreground">Refreshing SMSWiser catalog</span>
      <span className="text-muted-foreground">
        {progress.total
          ? `${progress.done}/${progress.total} countries`
          : `${progress.done} countries so far`}
        {progress.failed > 0 ? <> · {progress.failed} failed</> : null}
        {data.currentCountry ? <> · {data.currentCountry}</> : null}
      </span>
      <Link
        to="/admin/smswiser-catalog"
        className="ml-auto flex items-center gap-1.5 text-primary hover:underline"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        View progress
      </Link>
    </div>
  );
}
